import { observer } from 'mobx-react';
import classnames from 'classnames';

import style from './Avatar.module.scss';

import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { CancelButton } from '@components/ActionButtons/CancelButton';
import { SaveButton } from '@components/ActionButtons/SaveButton';
import { FixedIcon, Icon, IconType } from '@components/Icon';
import { FileInput } from '@components/FileInput';
import { Popup } from '@components/Popup';

import { AvatarUnits } from 'chaika-frontend-types/frontend-cms';

import { AvatarEditor } from './AvatarEditor';
import { useAvatar } from './hooks/useAvatar';
import { CommonTestAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

export interface AvatarProps {
    userId?: string;
    customSize?: boolean;
    overlayControls?: boolean;
    url?: string;
    avatarUnits?: AvatarUnits;
    onUpdate?: (url: string) => void;
    onAvatarUnitsSave?: (avatarUnits: AvatarUnits) => void;
}

export const Avatar = observer(function Avatar({ customSize, overlayControls, ...avatarProps }: AvatarProps) {
    const {
        url,
        avatarUnits,
        isEditingAvatar,
        onChangeClick,
        startAvatarEditing,
        changeAvatarUnits,
        cancelAvatarEditing,
        endAvatarEditing,
    } = useAvatar(avatarProps);

    const isEditable = !!onChangeClick;
    const isEditorOpen = !!url && isEditingAvatar;

    return (
        <>
            <div
                className={classnames([
                    style.avatar,
                    isEditable && style.big,
                    !url && style.noAvatar,
                    customSize && style.customSize,
                ])}
            >
                {url ? (
                    <img
                        className={style.image}
                        style={{
                            top: `${avatarUnits.offsetY}%`,
                            left: `${avatarUnits.offsetX}%`,
                            transform: `scale(${avatarUnits.scale})`,
                        }}
                        src={url}
                        alt={''}
                    />
                ) : (
                    FixedIcon.getDefaultAvatar()
                )}
                {overlayControls && isEditable && (
                    <div className={style.overlay}>
                        {url && (
                            <button
                                type={'button'}
                                className={style.overlayButton}
                                aria-label={translate('editPhoto')}
                                onClick={startAvatarEditing}
                            >
                                <Icon type={IconType.PENCIL} />
                            </button>
                        )}
                        <FileInput
                            withoutPaddings
                            onFileAdd={onChangeClick}
                        >
                            <button
                                type={'button'}
                                className={style.overlayButton}
                                aria-label={translate(url ? 'uploadNewPhoto' : 'addPhoto')}
                            >
                                <Icon type={IconType.PLUS} />
                            </button>
                        </FileInput>
                    </div>
                )}
            </div>
            {!overlayControls && onChangeClick && (
                <FileInput onFileAdd={onChangeClick}>
                    <Button
                        dataTest={CommonTestAttributes.Unknown}
                        theme={ButtonTheme.Link}
                        modifiers={[ButtonModifiers.WithoutLeftAndRightPaddings]}
                        label={translate(url ? 'uploadNewPhoto' : 'addPhoto')}
                    />
                </FileInput>
            )}
            {!overlayControls && url && isEditable && (
                <div>
                    <Button
                        dataTest={CommonTestAttributes.Unknown}
                        theme={ButtonTheme.Link}
                        modifiers={[ButtonModifiers.WithoutLeftAndRightPaddings]}
                        label={translate('editPhoto')}
                        onClick={startAvatarEditing}
                    />
                </div>
            )}
            {isEditorOpen && (
                <Popup onOutOfContentClick={cancelAvatarEditing}>
                    <div className={style.editor}>
                        <AvatarEditor
                            url={url}
                            avatarUnits={avatarUnits}
                            onChangeAvatarUnits={changeAvatarUnits}
                        />
                        <div className={style.actions}>
                            <SaveButton
                                dataTest={CommonTestAttributes.Unknown}
                                onSaveClick={endAvatarEditing}
                            />
                            <CancelButton
                                dataTest={CommonTestAttributes.Unknown}
                                onCancelClick={cancelAvatarEditing}
                            />
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
});
